import { Component, computed, inject, Input, input, OnChanges, OnInit } from '@angular/core'
import {
  CartState,
  Experiment,
  getBucket,
  ProductKey,
  ProductType,
  ProductVariant,
} from '@naturalcycles/shared'
import { TranslateService } from '@ngx-translate/core'
import {
  getAb344DiscountBucket,
  isAb344Test,
  isAb344Test2,
} from '@src/ab-344/ab344-experiment.utils.js'
import { ExperimentService } from '@src/app/core/services/experiment.service.js'
import { ModalService } from '@src/app/core/services/modal.service.js'
import { SignalStore } from '@src/app/core/store/signalStore.js'
import { Page } from '@src/app/shared/typings/enum/pages.js'

interface CartPriceAB344Configuration {
  shippingPriceText: string
  isAB344Active: boolean
}

@Component({
  selector: 'app-cart-price',
  templateUrl: './cart-price.component.html',
  styleUrls: ['./cart-price.component.scss'],
  standalone: false,
})
export class CartPriceComponent implements OnChanges, OnInit {
  private modalService = inject(ModalService)
  private store = inject(SignalStore)
  private translate = inject(TranslateService)
  private experimentService = inject(ExperimentService)

  private discountBucket = computed(() =>
    getAb344DiscountBucket(this.store.$experiment().assignments),
  )

  private isTest = computed(() => isAb344Test(this.discountBucket()))
  private isTest2 = computed(() => isAb344Test2(this.discountBucket()))

  public isTest364 = computed(
    () =>
      getBucket(this.store.$experiment().assignments[Experiment.QUIZ_OURA_DISCOUNT_CHECKOUT]) ===
      'test',
  )

  protected Page = Page

  @Input()
  public cart!: CartState

  @Input()
  protected hasBTThermometerInCart = false

  public hasShopItems?: boolean
  public wantsOuraDevice?: boolean

  public discountedAmountString?: string
  public isMonthlySubscription = false

  protected page = input<Page>()

  protected showOuraDiscountBannerAB364 = computed(() => {
    return this.page() === Page.payment && this.wantsOuraDevice && this.isTest364()
  })

  protected ab344Configuration = computed<CartPriceAB344Configuration>(() => {
    return {
      shippingPriceText: this.isTest()
        ? '$0'
        : this.isTest2()
          ? 'FREE'
          : this.translate.instant('tag-free'),
      isAB344Active: (this.isTest() || this.isTest2()) && this.hasBTThermometerInCart,
    }
  })

  async ngOnInit(): Promise<void> {
    const discountedAmount =
      this.cart?.totalPriceWithoutDiscount?.amount - this.cart?.totalPrice?.amount
    this.discountedAmountString = `-$${discountedAmount.toFixed(2)}`
    this.isMonthlySubscription = this.cart?.items.some(i => i.key === ProductKey.MONTH)

    if (this.hasBTThermometerInCart) {
      await this.experimentService.logImpression(Experiment.DISCOUNTS_ON_CHECKOUT_SCREEN)
    }

    if (this.wantsOuraDevice) {
      await this.experimentService.logImpression(Experiment.QUIZ_OURA_DISCOUNT_CHECKOUT)
    }
  }

  ngOnChanges(): void {
    this.hasShopItems = this.cart?.items.some(i => i.type === ProductType.SHOP)
    this.wantsOuraDevice = this.cart?.items.some(
      i => i.key === ProductKey.OURA_RING_DISCOUNT && i.variant === ProductVariant.wantsDevice,
    )
  }
}
